import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect, useMemo } from 'react';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { useNavigate } from '@tanstack/react-router';
import {
  ShoppingBag,
  Plus,
  Pencil,
  Trash2,
  Package,
  Zap,
  Users,
  TrendingUp,
  BarChart3,
  History,
  PackagePlus,
  Eye,
  Crown,
  Bell,
  Loader2,
  Trophy,
} from 'lucide-react';
import { storeService, type StoreProduct } from '@/features/store/services/store.service';
import { Permission } from '@/common/enums/permissions.enum';
import { usePermissions } from '@/common/hooks/usePermissions';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Pagination } from '@/components/ui/pagination';
import { Checkbox } from '@/components/ui/checkbox';
import { PointsOverviewTab } from '@/features/gamification/components/PointsOverviewTab';
import { UserPointsHistoryTab } from '@/features/gamification/components/UserPointsHistoryTab';
import { TeamProfilesTab } from '@/features/gamification/components/TeamProfilesTab';

const POINTS_TABS = ['insights', 'points-overview', 'points-history', 'team-profiles'] as const;
const STORE_TABS = ['products', 'history', 'notifications', 'exchange-rate'] as const;
type AdminStoreTab = (typeof POINTS_TABS)[number] | (typeof STORE_TABS)[number];

const DEFAULT_TAB_BY_PERMISSION: Record<'store' | 'points', AdminStoreTab> = {
  store: 'insights',
  points: 'points-overview',
};

export const Route = createFileRoute('/_authenticated/admin/store')({
  validateSearch: (search: Record<string, unknown>) => ({
    tab: typeof search.tab === 'string' ? search.tab : 'insights',
  }),
  component: AdminStorePage,
});

function AdminStorePage() {
  const { hasPermission } = usePermissions();
  const canManageStore = hasPermission(Permission.STORE_MANAGE);
  const canViewPoints = canManageStore || hasPermission(Permission.DASHBOARD_ADMIN);

  if (!canViewPoints) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Você não tem permissão para acessar esta página.</p>
      </div>
    );
  }

  return <AdminStoreContent canManageStore={canManageStore} canViewPoints={canViewPoints} />;
}

function AdminStoreContent({
  canManageStore,
  canViewPoints,
}: {
  canManageStore: boolean;
  canViewPoints: boolean;
}) {
  const queryClient = useQueryClient();
  const { tab } = Route.useSearch();
  const navigate = useNavigate();

  const allowedTabs = useMemo(() => {
    const tabs: AdminStoreTab[] = [];
    if (canViewPoints) {
      tabs.push(...POINTS_TABS);
    }
    if (canManageStore) {
      tabs.push(...STORE_TABS);
    }
    return tabs;
  }, [canManageStore, canViewPoints]);

  const defaultTab = canManageStore
    ? DEFAULT_TAB_BY_PERMISSION.store
    : DEFAULT_TAB_BY_PERMISSION.points;

  const activeTab = allowedTabs.includes(tab as AdminStoreTab)
    ? (tab as AdminStoreTab)
    : defaultTab;

  useEffect(() => {
    if (!allowedTabs.includes(tab as AdminStoreTab)) {
      navigate({ to: '/admin/store', search: { tab: defaultTab }, replace: true });
    }
  }, [allowedTabs, defaultTab, navigate, tab]);

  const setActiveTab = (value: string) => {
    navigate({ to: '/admin/store', search: { tab: value } });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-yellow-500/10 border border-yellow-500/30 rounded-md">
          <ShoppingBag className="h-6 w-6 text-yellow-500" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Loja de Pontos</h1>
          <p className="text-muted-foreground text-sm">
            Gestão da loja, análise de pontos e histórico de compras
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex flex-wrap h-auto gap-1 p-2">
          {canViewPoints && (
            <>
              <span className="w-full text-[10px] uppercase tracking-wider text-muted-foreground font-medium px-1">
                Pontos
              </span>
              <TabsTrigger value="insights">
                <BarChart3 className="h-4 w-4 mr-1.5" />
                Resumo
              </TabsTrigger>
              <TabsTrigger value="points-overview">
                <Trophy className="h-4 w-4 mr-1.5" />
                Visão Geral
              </TabsTrigger>
              <TabsTrigger value="points-history">
                <History className="h-4 w-4 mr-1.5" />
                Histórico por Usuário
              </TabsTrigger>
              <TabsTrigger value="team-profiles">
                <Users className="h-4 w-4 mr-1.5" />
                Perfis
              </TabsTrigger>
            </>
          )}
          {canManageStore && (
            <>
              <span className="w-full text-[10px] uppercase tracking-wider text-muted-foreground font-medium px-1 pt-2">
                Loja
              </span>
              <TabsTrigger value="products">
                <Package className="h-4 w-4 mr-1.5" />
                Produtos
              </TabsTrigger>
              <TabsTrigger value="history">
                <History className="h-4 w-4 mr-1.5" />
                Compras
              </TabsTrigger>
              <TabsTrigger value="notifications">
                <Bell className="h-4 w-4 mr-1.5" />
                Notificações
              </TabsTrigger>
              <TabsTrigger value="exchange-rate">
                <TrendingUp className="h-4 w-4 mr-1.5" />
                Cotação
              </TabsTrigger>
            </>
          )}
        </TabsList>

        {canViewPoints && (
          <>
            <TabsContent value="insights" className="mt-6">
              <InsightsTab />
            </TabsContent>
            <TabsContent value="points-overview" className="mt-6">
              <PointsOverviewTab />
            </TabsContent>
            <TabsContent value="points-history" className="mt-6">
              <UserPointsHistoryTab />
            </TabsContent>
            <TabsContent value="team-profiles" className="mt-6">
              <TeamProfilesTab />
            </TabsContent>
          </>
        )}

        {canManageStore && (
          <>
            <TabsContent value="products" className="mt-6">
              <ProductsTab queryClient={queryClient} />
            </TabsContent>
            <TabsContent value="history" className="mt-6">
              <HistoryTab />
            </TabsContent>
            <TabsContent value="notifications" className="mt-6">
              <NotificationsTab />
            </TabsContent>
            <TabsContent value="exchange-rate" className="mt-6">
              <ExchangeRateTab />
            </TabsContent>
          </>
        )}
      </Tabs>
    </div>
  );
}

// ===========================
// TAB INSIGHTS
// ===========================

function InsightsTab() {
  const { data: insights, isLoading } = useQuery({
    queryKey: ['store-admin-insights'],
    queryFn: storeService.getAdminInsights,
  });

  if (isLoading) return <div className="text-muted-foreground py-12 text-center">Carregando insights...</div>;
  if (!insights) return null;

  return (
    <div className="space-y-6">
      {/* Cards principais */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Users className="h-8 w-8 text-blue-500 shrink-0" />
              <div>
                <p className="text-2xl font-bold">{insights.usersWithPoints.length}</p>
                <p className="text-xs text-muted-foreground">Usuários com pontos</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Zap className="h-8 w-8 text-yellow-500 shrink-0" />
              <div>
                <p className="text-2xl font-bold">{insights.totalPointsInCirculation.toFixed(1)}</p>
                <p className="text-xs text-muted-foreground">Pontos em circulação</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Package className="h-8 w-8 text-emerald-500 shrink-0" />
              <div>
                <p className="text-2xl font-bold">{insights.totalActiveProducts}</p>
                <p className="text-xs text-muted-foreground">Produtos ativos</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <ShoppingBag className="h-8 w-8 text-purple-500 shrink-0" />
              <div>
                <p className="text-2xl font-bold">{insights.totalPurchases}</p>
                <p className="text-xs text-muted-foreground">Total de compras</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Máximo diário por tipo */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Pontos máximos diários por tipo
          </CardTitle>
          <CardDescription>Quantidade máxima de pontos que cada usuário pode ganhar por dia por tipo de tarefa</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {Object.entries(insights.dailyMaxByType).map(([type, data]) => {
              const pct = (data.maxDaily / insights.totalMaxDaily) * 100;
              const typeLabels: Record<string, string> = {
                whatsapp: 'WhatsApp', manual: 'Manual', notion: 'Notion', personal: 'Pessoal', calendar: 'Calendário', playbook: 'Playbook', tech_system: 'Sistema TEC',
              };
              const typeColors: Record<string, string> = {
                whatsapp: 'bg-green-500', manual: 'bg-orange-500', notion: 'bg-blue-500', personal: 'bg-pink-500', calendar: 'bg-cyan-500', playbook: 'bg-purple-500', tech_system: 'bg-indigo-500',
              };
              return (
                <div key={type} className="flex items-center gap-3">
                  <span className="text-sm font-medium w-24 shrink-0">{typeLabels[type] || type}</span>
                  <div className="flex-1 h-3 bg-muted rounded-full overflow-hidden">
                    <div className={`h-full ${typeColors[type] || 'bg-gray-500'} rounded-full`} style={{ width: `${pct}%` }} />
                  </div>
                  <div className="text-xs text-muted-foreground w-32 text-right shrink-0">
                    {data.points} pts × {data.dailyLimit} = <span className="font-bold text-foreground">{data.maxDaily.toFixed(1)} pts/dia</span>
                  </div>
                </div>
              );
            })}
            <div className="pt-2 border-t flex items-center justify-between text-sm">
              <span className="font-medium">Total máximo por dia</span>
              <span className="font-bold text-yellow-600 dark:text-yellow-400">{insights.totalMaxDaily.toFixed(1)} pts</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Ranking de usuários */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Crown className="h-5 w-5 text-yellow-500" />
            Ranking de Pontos
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">#</TableHead>
                <TableHead>Usuário</TableHead>
                <TableHead>Nível</TableHead>
                <TableHead className="text-right">Pontos</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {insights.usersWithPoints.slice(0, 15).map((user, idx) => (
                <TableRow key={user.userId}>
                  <TableCell className="font-bold text-muted-foreground">{idx + 1}</TableCell>
                  <TableCell className="text-sm">{user.email}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-xs">Lv. {user.level}</Badge>
                  </TableCell>
                  <TableCell className="text-right font-bold text-yellow-600 dark:text-yellow-400">
                    {Number(user.points).toFixed(1)} pts
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

// ===========================
// TAB PRODUTOS
// ===========================

export function ProductsTab({ queryClient }: { queryClient: any }) {
  const confirm = useConfirm();
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState<StoreProduct | null>(null);
  const [stockProduct, setStockProduct] = useState<StoreProduct | null>(null);
  const [historyProduct, setHistoryProduct] = useState<StoreProduct | null>(null);

  const { data: products = [], isLoading } = useQuery({
    queryKey: ['store-admin-products'],
    queryFn: storeService.adminGetProducts,
  });

  const deleteMutation = useMutation({
    mutationFn: storeService.deleteProduct,
    onSuccess: () => {
      toast.success('Produto excluído');
      queryClient.invalidateQueries({ queryKey: ['store-admin-products'] });
    },
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-muted-foreground text-sm">{products.length} produto{products.length !== 1 ? 's' : ''}</p>
        <Button onClick={() => { setEditingProduct(null); setShowForm(true); }} size="sm">
          <Plus className="h-4 w-4 mr-1.5" />
          Novo Produto
        </Button>
      </div>

      {isLoading ? (
        <div className="text-muted-foreground py-12 text-center">Carregando...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {products.map((product) => (
            <Card key={product.id} className={!product.active ? 'opacity-60' : ''}>
              <CardContent className="pt-4 space-y-3">
                <div className="flex gap-3">
                  {product.imageUrl ? (
                    <img src={product.imageUrl} alt={product.name} className="w-14 h-14 object-cover rounded-md border shrink-0" />
                  ) : (
                    <div className="w-14 h-14 bg-muted rounded-md border flex items-center justify-center shrink-0">
                      <Package className="h-6 w-6 text-muted-foreground" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-semibold text-sm truncate">{product.name}</h4>
                      {!product.active && <Badge variant="secondary" className="text-xs">Inativo</Badge>}
                    </div>
                    {product.description && <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{product.description}</p>}
                  </div>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-1 text-yellow-600 dark:text-yellow-400 font-bold">
                    <Zap className="h-3.5 w-3.5" />
                    {product.pointPrice} pts
                  </div>
                  <div className="text-muted-foreground">
                    Estoque: <span className={`font-bold ${product.availableQty <= 0 ? 'text-red-500' : 'text-foreground'}`}>{product.availableQty}</span>
                  </div>
                </div>

                {product.maxPerUser !== null && product.maxPerUser !== undefined && (
                  <p className="text-xs text-muted-foreground">Máx. {product.maxPerUser}x por usuário por lote</p>
                )}

                {(product as any).totalPurchases > 0 && (
                  <p className="text-xs text-muted-foreground">{(product as any).totalPurchases} compra{(product as any).totalPurchases !== 1 ? 's' : ''} realizadas</p>
                )}

                <div className="flex gap-1.5 pt-1 flex-wrap">
                  <Button size="sm" variant="outline" className="h-7 text-xs px-2" onClick={() => { setEditingProduct(product); setShowForm(true); }}>
                    <Pencil className="h-3 w-3 mr-1" />
                    Editar
                  </Button>
                  <Button size="sm" variant="outline" className="h-7 text-xs px-2" onClick={() => setStockProduct(product)}>
                    <PackagePlus className="h-3 w-3 mr-1" />
                    Estoque
                  </Button>
                  <Button size="sm" variant="outline" className="h-7 text-xs px-2" onClick={() => setHistoryProduct(product)}>
                    <Eye className="h-3 w-3 mr-1" />
                    Histórico
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs px-2 text-red-600 hover:text-red-700"
                    aria-label={`Excluir ${product.name}`}
                    onClick={async () => {
                      if (
                        await confirm({
                          title: `Excluir "${product.name}"?`,
                          description: 'Esta ação não pode ser desfeita.',
                          confirmLabel: 'Excluir produto',
                          destructive: true,
                        })
                      ) {
                        deleteMutation.mutate(product.id);
                      }
                    }}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Product Form Dialog */}
      {showForm && (
        <ProductFormDialog
          key={editingProduct?.id ?? 'new'}
          open
          onClose={() => { setShowForm(false); setEditingProduct(null); }}
          product={editingProduct}
          queryClient={queryClient}
        />
      )}

      {/* Add Stock Dialog */}
      {stockProduct && (
        <AddStockDialog
          product={stockProduct}
          onClose={() => setStockProduct(null)}
          queryClient={queryClient}
        />
      )}

      {/* Product History Dialog */}
      {historyProduct && (
        <ProductHistoryDialog
          product={historyProduct}
          onClose={() => setHistoryProduct(null)}
        />
      )}
    </div>
  );
}

function ProductFormDialog({
  open,
  onClose,
  product,
  queryClient,
}: {
  open: boolean;
  onClose: () => void;
  product: StoreProduct | null;
  queryClient: any;
}) {
  const [name, setName] = useState(product?.name ?? '');
  const [description, setDescription] = useState(product?.description ?? '');
  const [pointPrice, setPointPrice] = useState(product ? Number(product.pointPrice).toString() : '');
  const [costBrl, setCostBrl] = useState(product?.costBrl ? Number(product.costBrl).toString() : '');

  const { data: storeSettings } = useQuery({
    queryKey: ['store-settings'],
    queryFn: storeService.getStoreSettings,
  });
  const pointsPerBrl = storeSettings?.pointsPerBrl ?? null;

  const handleCostChange = (value: string) => {
    setCostBrl(value);
    const cost = parseFloat(value);
    if (pointsPerBrl != null && cost > 0) {
      setPointPrice((cost * pointsPerBrl).toFixed(1));
    }
  };
  const [availableQty, setAvailableQty] = useState(product ? product.availableQty.toString() : '0');
  const [maxPerUser, setMaxPerUser] = useState(product?.maxPerUser?.toString() ?? '');
  const [active, setActive] = useState(product?.active ?? true);
  const [imageFile, setImageFile] = useState<File | null>(null);

  const createMutation = useMutation({
    mutationFn: storeService.createProduct,
    onSuccess: async (newProduct) => {
      let uploadFailed = false;
      if (imageFile) {
        try {
          await storeService.uploadProductImage(newProduct.id, imageFile);
        } catch {
          uploadFailed = true;
        }
      }
      if (uploadFailed) toast.warning('Produto criado, mas a imagem não foi enviada.');
      else toast.success('Produto criado!');
      queryClient.invalidateQueries({ queryKey: ['store-admin-products'] });
      onClose();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: any }) => storeService.updateProduct(id, dto),
    onSuccess: async (updatedProduct) => {
      let uploadFailed = false;
      if (imageFile) {
        try {
          await storeService.uploadProductImage(updatedProduct.id, imageFile);
        } catch {
          uploadFailed = true;
        }
      }
      if (uploadFailed) toast.warning('Produto atualizado, mas a imagem não foi enviada.');
      else toast.success('Produto atualizado!');
      queryClient.invalidateQueries({ queryKey: ['store-admin-products'] });
      onClose();
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const dto = {
      name,
      description: description || undefined,
      pointPrice: parseFloat(pointPrice),
      costBrl: costBrl ? parseFloat(costBrl) : undefined,
      availableQty: parseInt(availableQty) || 0,
      maxPerUser: maxPerUser ? parseInt(maxPerUser) : undefined,
      active,
    };

    if (product) {
      updateMutation.mutate({ id: product.id, dto });
    } else {
      createMutation.mutate(dto);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent size="md" busy={createMutation.isPending || updateMutation.isPending}>
        <DialogHeader>
          <DialogTitle>{product ? 'Editar Produto' : 'Novo Produto'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Nome *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} required placeholder="Nome do produto" />
          </div>
          <div className="space-y-2">
            <Label>Descrição</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Descrição..." rows={2} />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Custo do prêmio (R$)</Label>
              <Input type="number" step="0.01" min="0" value={costBrl} onChange={(e) => handleCostChange(e.target.value)} placeholder="Ex: 50.00" />
              {pointsPerBrl != null ? (
                <p className="text-xs text-muted-foreground">Cotação: R$ 1 = {pointsPerBrl} pts (preço calculado automaticamente, editável)</p>
              ) : (
                <p className="text-xs text-muted-foreground">Defina a cotação na aba "Cotação" para calcular o preço automaticamente.</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Preço em pontos *</Label>
              <Input type="number" step="0.1" min="0.1" value={pointPrice} onChange={(e) => setPointPrice(e.target.value)} required placeholder="10" />
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Quantidade inicial</Label>
              <Input type="number" min="0" value={availableQty} onChange={(e) => setAvailableQty(e.target.value)} placeholder="0" />
            </div>
            <div className="space-y-2">
              <Label>Máx. por usuário por lote (vazio = sem limite)</Label>
              <Input type="number" min="1" value={maxPerUser} onChange={(e) => setMaxPerUser(e.target.value)} placeholder="Ex: 1" />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">Se o máximo por usuário for definido, cada vez que adicionar estoque novo, todos os usuários poderão comprar novamente.</p>
          <div className="space-y-2">
            <Label>Imagem do produto</Label>
            <Input type="file" accept="image/*" onChange={(e) => setImageFile(e.target.files?.[0] ?? null)} />
          </div>
          <div className="flex items-center gap-3">
            <Switch checked={active} onCheckedChange={setActive} />
            <Label>Produto ativo</Label>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button type="submit" loading={createMutation.isPending || updateMutation.isPending}>
              {product ? 'Salvar alterações' : 'Criar produto'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AddStockDialog({ product, onClose, queryClient }: { product: StoreProduct; onClose: () => void; queryClient: any }) {
  const [qty, setQty] = useState('');
  const [note, setNote] = useState('');

  const mutation = useMutation({
    mutationFn: () => storeService.addStock(product.id, parseInt(qty), note || undefined),
    onSuccess: (data) => {
      toast.success(`Estoque adicionado! Novo total: ${data.newQty}`);
      queryClient.invalidateQueries({ queryKey: ['store-admin-products'] });
      onClose();
    },
  });

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent size="sm" busy={mutation.isPending}>
        <DialogHeader>
          <DialogTitle>Adicionar estoque</DialogTitle>
          <DialogDescription>Produto: <strong>{product.name}</strong> (atual: {product.availableQty})</DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (qty) mutation.mutate();
          }}
        >
          <div className="space-y-2">
            <Label>Quantidade a adicionar *</Label>
            <Input type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)} placeholder="Ex: 10" required />
          </div>
          <div className="space-y-2">
            <Label>Observação</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ex: Reposição mensal" />
          </div>
          {product.maxPerUser !== null && product.maxPerUser !== undefined && (
            <p className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 p-2 rounded border border-amber-200 dark:border-amber-800">
              ⚠️ Este produto tem limite por usuário. Ao adicionar estoque, todos os usuários poderão comprar novamente.
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button type="submit" loading={mutation.isPending} disabled={!qty}>
              <PackagePlus className="h-4 w-4 mr-1.5" />
              Adicionar estoque
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ProductHistoryDialog({ product, onClose }: { product: StoreProduct; onClose: () => void }) {
  const { data, isLoading } = useQuery({
    queryKey: ['product-history', product.id],
    queryFn: () => storeService.getProductHistory(product.id),
  });

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>Histórico: {product.name}</DialogTitle>
        </DialogHeader>
        {isLoading ? (
          <div className="py-8 text-center text-muted-foreground">Carregando...</div>
        ) : (
          <div className="space-y-6">
            {/* Entradas de estoque */}
            <div>
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <PackagePlus className="h-4 w-4 text-emerald-500" />
                Entradas de Estoque ({data?.stockBatches?.length ?? 0})
              </h3>
              {data?.stockBatches?.length ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data</TableHead>
                      <TableHead>Qtd.</TableHead>
                      <TableHead>Por</TableHead>
                      <TableHead>Nota</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.stockBatches.map((b: any) => (
                      <TableRow key={b.id}>
                        <TableCell className="text-xs">{format(new Date(b.createdAt), "dd/MM/yyyy HH:mm", { locale: ptBR })}</TableCell>
                        <TableCell className="font-bold text-emerald-600">+{b.qtyAdded}</TableCell>
                        <TableCell className="text-xs">{b.addedByEmail ?? '-'}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{b.note ?? '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : <p className="text-muted-foreground text-sm">Nenhuma entrada</p>}
            </div>

            {/* Compras */}
            <div>
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <ShoppingBag className="h-4 w-4 text-purple-500" />
                Compras ({data?.purchases?.length ?? 0})
              </h3>
              {data?.purchases?.length ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data</TableHead>
                      <TableHead>Usuário</TableHead>
                      <TableHead className="text-right">Pts gastos</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.purchases.map((p: any) => (
                      <TableRow key={p.id}>
                        <TableCell className="text-xs">{format(new Date(p.createdAt), "dd/MM/yyyy HH:mm", { locale: ptBR })}</TableCell>
                        <TableCell className="text-xs">{p.userEmail ?? '-'}</TableCell>
                        <TableCell className="text-right text-xs font-bold text-red-500">-{p.pointsSpent}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : <p className="text-muted-foreground text-sm">Nenhuma compra</p>}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ===========================
// TAB HISTÓRICO GERAL
// ===========================

function HistoryTab() {
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ['store-admin-all-purchases', page],
    queryFn: () => storeService.adminGetAllPurchases(page, 20),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <History className="h-5 w-5" />
          Histórico de Compras
        </CardTitle>
        <CardDescription>
          Todas as compras realizadas na loja, em ordem cronológica
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : !data?.items?.length ? (
          <div className="text-center py-12 text-muted-foreground">
            Nenhuma compra registrada
          </div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Produto</TableHead>
                  <TableHead>Usuário</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead className="text-right">Pts</TableHead>
                  <TableHead>Participantes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((purchase) => (
                  <TableRow key={purchase.id}>
                    <TableCell className="text-xs whitespace-nowrap text-muted-foreground">
                      {format(new Date(purchase.createdAt), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {purchase.imageUrl ? (
                          <img
                            src={purchase.imageUrl}
                            alt={purchase.productName}
                            className="w-9 h-9 rounded border object-cover shrink-0"
                          />
                        ) : (
                          <div className="w-9 h-9 rounded border bg-muted flex items-center justify-center shrink-0">
                            <Package className="h-4 w-4 text-muted-foreground" />
                          </div>
                        )}
                        <span className="text-sm font-medium">{purchase.productName}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{purchase.userEmail ?? '—'}</TableCell>
                    <TableCell>
                      <Badge variant={purchase.purchaseType === 'group' ? 'secondary' : 'outline'}>
                        {purchase.purchaseType === 'group' ? 'Grupo' : 'Individual'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-bold text-red-500 text-sm">
                      -{Number(purchase.pointsSpent).toFixed(1)}
                    </TableCell>
                    <TableCell>
                      {purchase.purchaseType === 'group' && purchase.participants?.length ? (
                        <div className="space-y-1.5">
                          <Badge variant="outline" className="text-xs mb-1">
                            {purchase.participantCount} pessoa{purchase.participantCount !== 1 ? 's' : ''}
                          </Badge>
                          <ul className="space-y-1">
                            {purchase.participants.map((participant) => (
                              <li
                                key={participant.userId}
                                className="text-xs text-muted-foreground flex items-center justify-between gap-2"
                              >
                                <span className="truncate max-w-[140px]">
                                  {participant.email ?? 'Usuário'}
                                </span>
                                <span className="font-medium text-foreground shrink-0">
                                  {Number(participant.points).toFixed(1)} pts
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-xs">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {data.pagination.totalPages > 1 && (
              <div className="mt-4 flex justify-center">
                <Pagination
                  total={data.pagination.total}
                  page={data.pagination.page}
                  limit={data.pagination.limit}
                  onPageChange={setPage}
                />
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function NotificationsTab() {
  const queryClient = useQueryClient();
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);

  const { data, isLoading } = useQuery({
    queryKey: ['store-notification-settings'],
    queryFn: storeService.getNotificationSettings,
  });

  useEffect(() => {
    if (data?.userIds) {
      setSelectedUserIds(data.userIds);
    }
  }, [data?.userIds]);

  const saveMutation = useMutation({
    mutationFn: () => storeService.updateNotificationSettings(selectedUserIds),
    onSuccess: () => {
      toast.success('Configuração de notificações salva!');
      queryClient.invalidateQueries({ queryKey: ['store-notification-settings'] });
    },
  });

  const toggleUser = (userId: string) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId],
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5" />
          Notificações de Compra
        </CardTitle>
        <CardDescription>
          Usuários selecionados receberão notificação push sempre que um produto for comprado na loja (individual ou em grupo).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : !data?.availableUsers?.length ? (
          <p className="text-muted-foreground text-sm">Nenhum usuário disponível</p>
        ) : (
          <>
            <div className="border rounded-md divide-y max-h-96 overflow-y-auto">
              {data.availableUsers.map((user) => (
                <label
                  key={user.id}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-muted/50 cursor-pointer"
                >
                  <Checkbox
                    checked={selectedUserIds.includes(user.id)}
                    onCheckedChange={() => toggleUser(user.id)}
                  />
                  <div>
                    <p className="text-sm font-medium">{user.email}</p>
                    {user.fullName && (
                      <p className="text-xs text-muted-foreground">{user.fullName}</p>
                    )}
                  </div>
                </label>
              ))}
            </div>
            <div className="flex items-center justify-between pt-2">
              <p className="text-sm text-muted-foreground">
                {selectedUserIds.length} usuário{selectedUserIds.length !== 1 ? 's' : ''} selecionado{selectedUserIds.length !== 1 ? 's' : ''}
              </p>
              <Button
                onClick={() => saveMutation.mutate()}
                disabled={saveMutation.isPending}
              >
                {saveMutation.isPending ? 'Salvando...' : 'Salvar configuração'}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ===========================
// TAB COTAÇÃO (visível apenas para admins com store.manage)
// ===========================

function ExchangeRateTab() {
  const queryClient = useQueryClient();
  const [rateInput, setRateInput] = useState('');
  const [budgetInput, setBudgetInput] = useState('');
  const [factorInput, setFactorInput] = useState('3');
  const [suggestQuery, setSuggestQuery] = useState<{ budget: number; factor: number } | null>(null);

  const { data: settings, isLoading } = useQuery({
    queryKey: ['store-settings'],
    queryFn: storeService.getStoreSettings,
  });

  useEffect(() => {
    if (settings?.pointsPerBrl != null) {
      setRateInput(settings.pointsPerBrl.toString());
    }
  }, [settings?.pointsPerBrl]);

  const { data: suggestion, isFetching: isSuggesting } = useQuery({
    queryKey: ['store-suggested-rate', suggestQuery],
    queryFn: () => storeService.getSuggestedRate(suggestQuery!.budget, suggestQuery!.factor),
    enabled: suggestQuery !== null && suggestQuery.budget > 0,
  });

  const saveMutation = useMutation({
    mutationFn: (pointsPerBrl: number) => storeService.updateStoreSettings(pointsPerBrl),
    onSuccess: () => {
      toast.success('Cotação salva!');
      queryClient.invalidateQueries({ queryKey: ['store-settings'] });
    },
  });

  const parsedRate = parseFloat(rateInput);
  const currentRate = settings?.pointsPerBrl ?? null;

  return (
    <div className="space-y-6 max-w-2xl">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Cotação Ponto ↔ Real
          </CardTitle>
          <CardDescription>
            Define quantos pontos equivalem a R$ 1,00. Usada para calcular automaticamente o
            preço em pontos ao cadastrar um prêmio pelo custo em reais. Alterar a cotação não
            muda o preço de produtos já cadastrados. Visível apenas para administradores.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              <div className="flex items-end gap-3">
                <div className="space-y-2 flex-1">
                  <Label>Pontos por R$ 1,00</Label>
                  <Input
                    type="number"
                    step="0.0001"
                    min="0.0001"
                    value={rateInput}
                    onChange={(e) => setRateInput(e.target.value)}
                    placeholder="Ex: 20"
                  />
                </div>
                <Button
                  onClick={() => saveMutation.mutate(parsedRate)}
                  disabled={!parsedRate || parsedRate <= 0 || saveMutation.isPending}
                >
                  {saveMutation.isPending ? 'Salvando...' : 'Salvar cotação'}
                </Button>
              </div>
              {currentRate != null ? (
                <p className="text-sm text-muted-foreground">
                  Cotação atual: <span className="font-bold text-foreground">R$ 1,00 = {currentRate} pts</span>
                  {' · '}um prêmio de R$ 100 custa{' '}
                  <span className="font-bold text-yellow-600 dark:text-yellow-400">
                    {(100 * currentRate).toFixed(0)} pts
                  </span>
                </p>
              ) : (
                <p className="text-sm text-amber-600 dark:text-amber-400">
                  Nenhuma cotação definida ainda. Use a sugestão abaixo como ponto de partida.
                </p>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Sugestão baseada no esforço real
          </CardTitle>
          <CardDescription>
            Calcula a cotação pela emissão real de pontos do time nos últimos 90 dias,
            multiplicada por um fator de segurança anti-farm, dividida pelo orçamento total.
            A emissão pode crescer até o fator antes de o custo encostar no orçamento.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-end gap-3">
            <div className="space-y-2 flex-1">
              <Label>Orçamento total de prêmios por mês (R$)</Label>
              <Input
                type="number"
                step="1"
                min="1"
                value={budgetInput}
                onChange={(e) => setBudgetInput(e.target.value)}
                placeholder="Ex: 500"
              />
            </div>
            <div className="space-y-2 w-40">
              <Label>Fator de segurança</Label>
              <Input
                type="number"
                step="0.5"
                min="1"
                max="100"
                value={factorInput}
                onChange={(e) => setFactorInput(e.target.value)}
                placeholder="Ex: 3"
              />
            </div>
            <Button
              variant="outline"
              onClick={() =>
                setSuggestQuery({
                  budget: parseFloat(budgetInput),
                  factor: parseFloat(factorInput) || 3,
                })
              }
              disabled={!parseFloat(budgetInput) || isSuggesting}
            >
              {isSuggesting ? 'Calculando...' : 'Calcular sugestão'}
            </Button>
          </div>

          {suggestion && (
            suggestion.suggestedPointsPerBrl == null ? (
              <p className="text-sm text-muted-foreground">
                Sem emissão de pontos nos últimos {suggestion.periodDays} dias — não há
                dados para sugerir uma cotação.
              </p>
            ) : (
              <div className="rounded-md border bg-muted/40 p-4 space-y-2 text-sm">
                <p>
                  O time ({suggestion.userCount} usuário{suggestion.userCount !== 1 ? 's' : ''}) ganhou{' '}
                  <span className="font-bold text-yellow-600 dark:text-yellow-400">
                    {suggestion.teamPointsPerMonth.toFixed(1)} pts/mês
                  </span>{' '}
                  nos últimos {suggestion.periodDays} dias. Com folga de {suggestion.safetyFactor}×, o cálculo
                  considera até {(suggestion.teamPointsPerMonth * suggestion.safetyFactor).toFixed(0)} pts/mês
                  (teto teórico pelos limites diários: {suggestion.maxTeamPointsPerMonth.toFixed(0)} pts/mês).
                </p>
                <p>
                  Com orçamento total de R$ {suggestion.monthlyBudgetBrl.toFixed(2)}/mês, a cotação sugerida é{' '}
                  <span className="font-bold text-foreground">
                    R$ 1,00 = {suggestion.suggestedPointsPerBrl.toFixed(2)} pts
                  </span>
                  .
                </p>
                <Button
                  size="sm"
                  className="mt-1"
                  onClick={() => setRateInput(suggestion.suggestedPointsPerBrl!.toFixed(4))}
                >
                  Usar sugestão
                </Button>
              </div>
            )
          )}
        </CardContent>
      </Card>
    </div>
  );
}
